import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { toStripeRedirectUrl } from '../utils/stripe-redirect-utils';
import { PortalLink } from '../response-models/portal-link.model';
import { SubscriptionCheckoutSession } from '../response-models/subscription-checkout-session.model';

/**
 * Subscription billing actions — Upgrade to Pro (Task Group 8) and Manage
 * Subscription (Task Group 9). Both endpoints already exist and are reused
 * as-is (FR-4.2/FR-5.2); this service just wraps them for the web app.
 *
 * Neither call needs an explicit `returnPath` — the Hub's own defaults
 * already point at this ticket's routes: checkout-session's default is
 * `/billing/subscription/return` (`buildReturnUrls`'s WEB_DEFAULT_PATHS),
 * portal-link's default is `/billing` (both confirmed directly in
 * user-payments-controller.ts). Passing them explicitly would just
 * duplicate what the Hub already assumes.
 *
 * Per billing-payments § 1.0 (Q0): the web NEVER imports `@stripe/stripe-js`.
 * All Stripe interaction is via hosted-page redirect, matching
 * `MapAnalysisCheckoutService`'s existing pattern.
 *
 * Failures reject with an `ApiError` (`message` is the hub's error).
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionBillingService extends ApiService {
  constructor() {
    super('payments/subscriptions');
  }

  async createCheckoutSession(): Promise<SubscriptionCheckoutSession> {
    return this.postForUrl<SubscriptionCheckoutSession>('checkout-session', 'Failed to create checkout session');
  }

  async createPortalLink(): Promise<PortalLink> {
    return this.postForUrl<PortalLink>('portal-link', 'Failed to create portal link');
  }

  /**
   * Redirects the browser to a Stripe-hosted URL (Checkout or Portal).
   * `url` comes from the Hub's response body, and this is the single choke
   * point every redirect in this service goes through, so it's the cheapest
   * place to guard against a malformed/compromised response turning into an
   * open-redirect, phishing, or `javascript:`-URL vector: enforces `https:`
   * AND that the host is Stripe's own domain (this service only ever redirects
   * to Stripe-hosted Checkout/Portal pages — an arbitrary-but-https domain is
   * still the wrong destination) (Copilot catches on PR #108).
   */
  redirect(url: string): void {
    window.location.assign(toStripeRedirectUrl(url));
  }

  /** POST `/payments/subscriptions/{path}`; the result must carry a Stripe `url`. */
  private async postForUrl<R extends { url: string }>(path: string, failure: string): Promise<R> {
    const result = await firstValueFrom(this.request('POST', { path }, data => data as R | null, {}));
    if (!result?.url) {
      throw new Error(failure);
    }
    return result;
  }
}
